import React, { FunctionComponent, useCallback, useState } from 'react';
import { useCookies } from 'react-cookie';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';
import { SgwtAccountCenter } from '@sgwt/sgwt-widgets-react';
import frenchFlag from '../../assets/images/la-france.png';
import britishFlag from '../../assets/images/united-kingdom.png';
import {
  availableDesksRoute,
  MapsFromAvailableDesks,
  reservationRoute,
  servicesRoute,
  smartBuildingRoute,
} from '../../constants/ApiRoutes';
import { ORDERED_FEATURES } from '../../pages/SiteServices/SiteServices';
import { useStoreActions, useStoreState } from '../../store/hooks';
import SwitchLanguage from './SwitchLanguage/SwitchLanguage.component';

import './Header.css';

const Header: FunctionComponent = () => {
  const location = useLocation();
  const [current, setCurrent] = useState('accueil');
  const currentTranslation = useStoreState((state) => state.intl.locale);
  const updateIntl = useStoreActions((actions) => actions.intl.update);
  const locale = useStoreState((state) => state.intl.locale);
  const [cookies, setCookie] = useCookies<'language', CookieValues>(['language']);

  const site = useStoreState((state) => state.sites);

  const resetStoreSelectedSite = useStoreActions((actions) => actions.sites.setSelectSite);

  const resetSelectedSite = useCallback(() => {
    resetStoreSelectedSite('UNDEFINED');
  }, [resetStoreSelectedSite]);

  const translationFlag = currentTranslation === 'fr' ? frenchFlag : britishFlag;
  const translationCountry = currentTranslation === 'fr' ? 'FR' : 'EN';

  const toggleActive = (service: string) => {
    setCurrent(service);
  };

  const toggleWelcomeActive = useCallback(() => {
    toggleActive('accueil');
  }, []);

  function toService(service: string) {
    switch (service) {
      case 'domotique':
        return smartBuildingRoute;
      case 'bureauxDispo':
        return availableDesksRoute;
      default:
        return reservationRoute;
    }
  }

  function toCSS(service: string) {
    if (service === 'bureauxDispo' && location.pathname === MapsFromAvailableDesks) {
      return "navbar-link active";
    }
    return "navbar-link";
  }

  React.useEffect(() => {
    const getCurrentLocationService = () => {
      switch (location.pathname) {
        case servicesRoute:
          return 'accueil';
        case smartBuildingRoute:
          return 'domotique';
        case availableDesksRoute:
          return 'bureauxDispo';
        case MapsFromAvailableDesks:
          return 'bureauxDispo';
        default:
          return 'accueil';
      }
    };
    setCurrent(getCurrentLocationService());
  }, [location]);

  const onLanguageChanged: EventListener = (event: any) => {
    updateIntl(event);
    setCookie('language', event);
  };

  React.useEffect(() => {
    const eventName = `sgwt-account-center--language-changed`;
    const languageChanged = (evt: any) => onLanguageChanged(evt.detail.language);
    document.addEventListener(eventName, languageChanged);
    return () => document.removeEventListener(eventName, languageChanged);
  }, [onLanguageChanged]);

  React.useEffect(() => {
    updateIntl(cookies['language'] || locale);
  }, [cookies, locale, updateIntl]);

  return (
    <nav className="navbar border-bottom border-top">
      <div className="navbar-title">
        <NavLink to="/" className="navbar-title-link" onClick={resetSelectedSite}>
          <div className="navbar-logo">
            <img data-testid="sg-logo" src="/favicon.svg" alt="SG" className="border border-white" />
          </div>
        </NavLink>
      </div>
      <div className="navbar-content">
        <ul className="navbar-navigation m-0">
          {location.pathname !== '/' && (
            <li className="navbar-item">
              <NavLink to="/" className="navbar-link" onClick={resetSelectedSite}>
                <FormattedMessage id="header.changeSite" />
              </NavLink>
            </li>
          )}
          {site.selectedSite !== 'UNDEFINED' && (
            <li className="navbar-item">
              <NavLink to="/services" className="navbar-link" onClick={toggleWelcomeActive}>
                <FormattedMessage id="header.home" />
              </NavLink>
            </li>
          )}
          {site.selectedSite !== 'UNDEFINED' &&
            Object.keys(site.siteServices)
              .sort((a, b) => {
                return ORDERED_FEATURES[a] - ORDERED_FEATURES[b];
              })
              .filter(
                (service) =>
                  (service === 'domotique' && site.siteServices.domotique) ||
                  (service === 'activateBooking' && site.siteServices.activateBooking) ||
                  (service === 'bureauxDispo' && site.siteServices.bureauxDispo)
              )
              .map((service) => (
                <li className="navbar-item" key={`header-shortcut-link-${service}`}>
                  <NavLink to={toService(service)} className={toCSS(service)}>
                    <FormattedMessage id={`servicesTitles.${service}`} />
                  </NavLink>
                </li>
              ))}
        </ul>
      </div>
      <SwitchLanguage translationFlag={translationFlag} translationCountry={translationCountry} />
      <SgwtAccountCenter hideServicesLink availableLanguages={['en', 'fr']} authentication="sg-connect" />
    </nav>
  );
};

export default Header;
